import { Component, OnInit, output, input } from "@angular/core";
import { Router } from '@angular/router';
import { ADPResourceService } from 'digital-core';
import { NgStyle } from "@angular/common";
import { MatIcon } from "@angular/material/icon";

type Style = Partial<CSSStyleDeclaration>;

@Component({
    selector: "pgh-avatar",
    templateUrl: "./avatar.component.html",
    styleUrls: ['./avatar.component.scss'],
    imports: [NgStyle, MatIcon]
})

export class AvatarComponent implements OnInit {
  public readonly round = input(true);
  public readonly size = input<string | number>(50);
  public readonly textSizeRatio = input(3);
  public readonly bgColor = input<string | undefined>(undefined);
  public readonly fgColor = input('#FFF');
  public readonly borderColor = input<string | undefined>(undefined);
  public readonly style = input<Style>({});
  public readonly cornerRadius = input<string | number>(0);
  public readonly color = input<string | null>(undefined);
  public readonly initials = input<string | null>(undefined, { alias: "name" });
  public readonly initialsSize = input<string | number>(2);
  // Opt-in per usage (header profile only) — renders a user silhouette instead of initials.
  public readonly showUserIcon = input(false);
  public readonly clickOnAvatar = output<any>();
  public avatarStyle: Style = {};
  public hostStyle: Style = {};
  public avatarText: string | null = null;
  public showGenericAvatar: boolean = false;

  private colors: string[] = [];
  private assignedColors: { [initial: string]: string } = {};

  private users: { name: string, initials: string, color: string }[]=[];

  constructor(public resourcesService: ADPResourceService, private router: Router) {
  }

  ngOnInit() {
    this.showGenericAvatar = this.resourcesService.getPortalSetting('Global_Avatar_Show_Generic_Avatar_Enabled')?.Value === 'true';
    this.colors = this.resourcesService.getPortalSetting('Global_Avatar_Colors').Value.split(',');
    this.initializeAvatar();
    this.buildTextAvatar();
  }

  private buildTextAvatar(): void {
    this.avatarText = this.getAvatar(+this.initialsSize());

    this.users.push({name:this.initials(), initials:this.avatarText, color:this.color()});
    this.assignColors(this.users);

    this.avatarStyle = this.getInitialsStyle(this.avatarText);

  }

  private initializeAvatar(): void {

      this.hostStyle = {
        width: this.size() + 'px',
        height: this.size() + 'px'
      };
  }

  /**
   *
   * returns initials style
   *
   * memberOf AvatarComponent
   */
  private getInitialsStyle(initials: string): Style {
    const borderColor = this.borderColor();
    const bgColor = this.bgColor();
    return {
      textAlign: 'center',
      borderRadius: this.round() ? '100%' : this.cornerRadius() + 'px',
      border: borderColor ? '1px solid ' + borderColor : '',
      textTransform: 'uppercase',
      color: this.fgColor(),
      width: this.size() + 'px',
      height: this.size() + 'px',
      backgroundColor: bgColor
        ? bgColor
        : this.getColorForInitial(initials),
      font:
      Math.floor(+this.size() / this.textSizeRatio()) +
        'px Open Sans, sans-serif',
      lineHeight: this.size() + 'px',
      ...this.style()
    };
  }

  public assignColors(users: { name: string, initials: string, color: string }[]) {
    users.forEach(user => {
      const initial = user.initials.toUpperCase();

      if(user.color === null || user.color === undefined || user.color === '')
      {
        if (!this.assignedColors[initial]) {
          const colorIndex = (initial.charCodeAt(0) + initial.charCodeAt(1)) % this.colors.length;
          this.assignedColors[initial] = this.colors[colorIndex];
        } else {
          let colorIndex = this.colors.indexOf(this.assignedColors[initial]) + 1;
          if (colorIndex >= this.colors.length) {
            colorIndex = 0;
          }
          while (Object.values(this.assignedColors).includes(this.colors[colorIndex])) {
            colorIndex = (colorIndex + 1) % this.colors.length;
          }
          this.assignedColors[initial] = this.colors[colorIndex];
        }
      }else{
        this.assignedColors[initial] = user.color;
      }
    });
  }

  getColorForInitial(initial: string): string {
    const capitalizedInitial = initial.toUpperCase();
    return this.assignedColors[capitalizedInitial] || '';
  }

  public getAvatar(size: number): string {
    return this.getInitials(this.initials(), size);
  }

  /**
   * Returns the initial letters of a name in a string
   * if the size is 2 it will take the first and the last
   */
  private getInitials(name: string, size: number): string {
    name = name?.trim();

    if (!name) {
      return '';
    }

    const initials = name.split(' ');

    if(size && size === 2 && initials.length >= size){
      const firstAndLast = [initials[0], initials[initials.length-1]];
      return this.constructInitials(firstAndLast);
    }

    if (size && size < initials.length) {
      return this.constructInitials(initials.slice(0, size));
    } else {      
      return this.constructInitials(initials);
    }
  }

  /**
   * Iterates a person's name string to get the initials of each word in uppercase.
   */
  private constructInitials(elements: string[]): string {

    if (!elements || !elements.length) {
      return '';
    }

    return elements
      .filter(element => element && element.length > 0)
      .map(element => element[0].toUpperCase())
      .join('');
  }

  get initialsLettersAvatarText() : string {
            return this.avatarText && this.avatarText.length > 0 ? this.avatarText[0]+ this.avatarText[this.avatarText.length - 1]  : '';
  }
}

